import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type CertificadoInfo, type LeituraCertificado } from "../api";
import type { Row } from "../types";
import { IconClose, IconUpload, IconTrash, IconFile, IconCheck } from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * Calibração de um equipamento de ensaio.
 *
 * Duas formas de informar a data, lado a lado:
 *  1. digitar a data de calibração (e a validade) direto nos campos;
 *  2. anexar o PDF do certificado do laboratório — o sistema lê o documento,
 *     acha a data de calibração e preenche os campos para conferência.
 *
 * O certificado real não traz data de validade, só a de calibração: por isso a
 * validade é calculada pelo PERÍODO de calibração (12 meses por padrão) e fica
 * editável. Nada é gravado antes de a pessoa conferir e clicar em Salvar.
 */

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

/** Compara textos ignorando caixa, acento, espaço e pontuação. */
function mesmoTexto(a: string | null | undefined, b: string | null | undefined): boolean {
  const limpar = (s: string | null | undefined) =>
    String(s ?? "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const x = limpar(a);
  const y = limpar(b);
  return !!x && !!y && (x === y || x.includes(y) || y.includes(x));
}

const PERIODOS = [
  { meses: 12, label: "12 meses" },
  { meses: 24, label: "24 meses" },
  { meses: 36, label: "36 meses" },
  { meses: 6, label: "6 meses" },
];

interface Props {
  equipamento: Row;
  onClose: () => void;
  onSaved: () => void;
}

export default function CertificadoCalibracaoModal({ equipamento, onClose, onSaved }: Props) {
  // Anexar certificado continua liberado; apagar o anexo é só de administrador.
  const podeExcluir = usePodeExcluir();
  const [calibracao, setCalibracao] = useState<string>(String(equipamento.data_calibracao ?? "").slice(0, 10));
  const [validade, setValidade] = useState<string>(String(equipamento.data_validade ?? "").slice(0, 10));
  const [meses, setMeses] = useState(12);
  const [numero, setNumero] = useState("");

  const [arquivo, setArquivo] = useState<File | null>(null);
  const [leitura, setLeitura] = useState<LeituraCertificado | null>(null);
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [anexos, setAnexos] = useState<CertificadoInfo[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const carregarAnexos = useCallback(async () => {
    try {
      setAnexos(await api.certificados.list(equipamento.id));
    } catch {
      /* a lista de anexos é secundária: não trava a tela */
    }
  }, [equipamento.id]);

  useEffect(() => {
    carregarAnexos();
  }, [carregarAnexos]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Anexou o PDF: manda ler e preenche os campos com o que voltou. */
  async function escolherArquivo(file: File | null) {
    if (!file) return;
    setArquivo(file);
    setErro(null);
    setLeitura(null);
    setLendo(true);
    try {
      const r = await api.certificados.ler(equipamento.id, file, meses);
      setLeitura(r);
      if (r.data_calibracao) setCalibracao(r.data_calibracao);
      if (r.data_validade) setValidade(r.data_validade);
      if (r.numero_certificado) setNumero(r.numero_certificado);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível ler o PDF.");
    } finally {
      setLendo(false);
    }
  }

  /** Mudou o período: recalcula a validade a partir da data de calibração. */
  function aplicarPeriodo(novoMeses: number) {
    setMeses(novoMeses);
    if (!calibracao) return;
    const [a, m, d] = calibracao.split("-").map(Number);
    if (!a || !m || !d) return;
    const base = new Date(Date.UTC(a, m - 1 + novoMeses, d, 12));
    if (base.getUTCDate() !== d) base.setUTCDate(0); // 31 → mês de 30
    setValidade(base.toISOString().slice(0, 10));
  }

  async function salvar() {
    setErro(null);
    if (!calibracao && !validade) {
      setErro("Informe a data de calibração (ou ao menos a validade).");
      return;
    }
    if (calibracao && validade && validade < calibracao) {
      setErro("A validade não pode ser anterior à data de calibração.");
      return;
    }
    setSalvando(true);
    try {
      await api.certificados.salvar(equipamento.id, {
        data_calibracao: calibracao,
        data_validade: validade,
        numero: numero || null,
        lidoDoPdf: !!leitura?.ok,
        file: arquivo,
      });
      onSaved();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível salvar.");
      setSalvando(false);
    }
  }

  /**
   * Abre o PDF numa aba nova. Era um `<a href>` — o navegador não manda o
   * usuário da sessão num link e o servidor respondia 401. Agora vai por
   * fetch + blob, o mesmo caminho da aba Equipamentos de Ensaio.
   */
  async function abrirAnexo(cert: CertificadoInfo) {
    try {
      await api.equipamentosEnsaio.abrirCertificado(cert.id);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível abrir o certificado.");
    }
  }

  async function removerAnexo(cert: CertificadoInfo) {
    if (!confirm(`Remover o arquivo "${cert.nome_arquivo}"?\n\nAs datas do equipamento não mudam.`)) return;
    try {
      await api.certificados.remover(cert.id);
      await carregarAnexos();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível remover o anexo.");
    }
  }

  // Conferência: o certificado é mesmo deste equipamento?
  const serieBate = leitura?.numero_serie
    ? mesmoTexto(leitura.numero_serie, String(equipamento.numero_serie ?? ""))
    : null;

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div
        className="modal-card modal-lg"
        role="dialog"
        aria-modal="true"
        aria-label={`Calibração de ${equipamento.nome}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>Calibração — {String(equipamento.nome ?? "")}</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Fechar" title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <p className="hint">
            {String(equipamento.numero_serie ?? "") ? (
              <>
                Nº de série <strong>{String(equipamento.numero_serie)}</strong> ·{" "}
              </>
            ) : null}
            Calibração atual: <strong>{dataBR(equipamento.data_calibracao)}</strong> · Validade:{" "}
            <strong>{dataBR(equipamento.data_validade)}</strong>
          </p>

          {/* 1) Anexar o certificado e deixar o sistema ler a data. Some para
              quem é somente visualização ("Escolher PDF" escapa do filtro por
              texto) — os anexos continuam listados abaixo para consulta. */}
          <div className="cert-bloco modo-leitura-esconde">
            <h4 className="cert-titulo">Anexar o certificado (PDF)</h4>
            <p className="hint">
              O sistema lê o documento e preenche a data de calibração sozinho. Se o PDF for
              digitalizado (só imagem), a data pode ser digitada abaixo — o arquivo é anexado do
              mesmo jeito.
            </p>
            <div className="cert-anexar">
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: "none" }}
                onChange={(e) => escolherArquivo(e.target.files?.[0] ?? null)}
              />
              <button type="button" className="btn-secondary" onClick={() => inputRef.current?.click()} disabled={lendo}>
                <IconUpload size={16} /> {arquivo ? "Trocar arquivo" : "Escolher PDF"}
              </button>
              {arquivo && (
                <span className="cert-arquivo">
                  <IconFile size={15} /> {arquivo.name}
                </span>
              )}
              {lendo && <span className="muted">Lendo o PDF…</span>}
            </div>

            {leitura && (
              <div className={`cert-leitura${leitura.ok ? "" : " cert-leitura-falhou"}`}>
                {leitura.ok ? (
                  <p className="cert-leitura-ok">
                    <IconCheck size={15} /> Data de calibração encontrada:{" "}
                    <strong>{dataBR(leitura.data_calibracao)}</strong>
                  </p>
                ) : null}
                {leitura.aviso && <p className="cert-aviso">{leitura.aviso}</p>}
                <ul className="cert-campos">
                  {leitura.numero_certificado && (
                    <li>
                      <span>Certificado</span> {leitura.numero_certificado}
                    </li>
                  )}
                  {leitura.numero_serie && (
                    <li>
                      <span>Nº de série no PDF</span> {leitura.numero_serie}
                      {serieBate === true && <em className="cert-bate"> confere</em>}
                      {serieBate === false && <em className="cert-difere"> diferente do cadastro</em>}
                    </li>
                  )}
                  {leitura.equipamento && (
                    <li>
                      <span>Equipamento no PDF</span> {leitura.equipamento}
                    </li>
                  )}
                  {leitura.fabricante && (
                    <li>
                      <span>Fabricante</span> {leitura.fabricante}
                    </li>
                  )}
                </ul>
                {leitura.datas_encontradas.length > 1 && (
                  <p className="hint">
                    Outras datas no documento:{" "}
                    {leitura.datas_encontradas.map((d) => (
                      <button
                        key={d}
                        type="button"
                        className="cert-data-alt"
                        onClick={() => {
                          setCalibracao(d);
                          aplicarPeriodo(meses);
                        }}
                        title="Usar esta data como calibração"
                      >
                        {dataBR(d)}
                      </button>
                    ))}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* 2) Datas (digitadas ou vindas do PDF) */}
          <div className="cert-bloco">
            <h4 className="cert-titulo">Datas</h4>
            <div className="cert-datas">
              <label className="field">
                <span>Data de calibração</span>
                <input
                  type="date"
                  value={calibracao}
                  onChange={(e) => {
                    setCalibracao(e.target.value);
                    if (e.target.value) {
                      const [a, m, d] = e.target.value.split("-").map(Number);
                      const base = new Date(Date.UTC(a!, m! - 1 + meses, d!, 12));
                      if (base.getUTCDate() !== d) base.setUTCDate(0);
                      setValidade(base.toISOString().slice(0, 10));
                    }
                  }}
                />
              </label>
              <label className="field">
                <span>Período de calibração</span>
                <select value={meses} onChange={(e) => aplicarPeriodo(Number(e.target.value))}>
                  {PERIODOS.map((p) => (
                    <option key={p.meses} value={p.meses}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Validade</span>
                <input type="date" value={validade} onChange={(e) => setValidade(e.target.value)} />
              </label>
              <label className="field">
                <span>Nº do certificado</span>
                <input
                  type="text"
                  value={numero}
                  placeholder="opcional"
                  onChange={(e) => setNumero(e.target.value)}
                />
              </label>
            </div>
            <p className="hint">
              O certificado do laboratório traz apenas a data de calibração — a validade é calculada
              pelo período acima e pode ser ajustada.
            </p>
          </div>

          {/* 3) Histórico de certificados já anexados */}
          {anexos.length > 0 && (
            <div className="cert-bloco">
              <h4 className="cert-titulo">Certificados anexados</h4>
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Arquivo</th>
                    <th>Calibração</th>
                    <th>Validade</th>
                    <th>Enviado por</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {anexos.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <button
                          type="button"
                          className="btn-link"
                          title="Abrir o PDF"
                          onClick={() => void abrirAnexo(c)}
                        >
                          {c.nome_arquivo}
                        </button>
                        {c.numero ? <span className="badge badge-soft">{c.numero}</span> : null}
                      </td>
                      <td>{dataBR(c.data_calibracao)}</td>
                      <td>{dataBR(c.data_validade)}</td>
                      <td>{c.enviado_por || "—"}</td>
                      <td className="col-actions">
                        {podeExcluir && (
                          <button
                            className="icon-btn icon-btn-danger"
                            title="Remover anexo"
                            onClick={() => removerAnexo(c)}
                          >
                            <IconTrash size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {erro && <div className="form-error">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={salvando}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={salvar} disabled={salvando || lendo}>
            {salvando ? "Salvando…" : arquivo ? "Salvar e anexar" : "Salvar datas"}
          </button>
        </div>
      </div>
    </div>
  );
}
